<template>
    <tr @click="loadOrder(order.order_id)" :id="'order-'+order.order_id" style="cursor: pointer">
        <td>
            <div>
                <team-assignment-update
                    :team_assigned_user="order.team_assigned_user"
                    :team_assigned_members="teamAssignmentMembers"
                    :reload_after_update="false"
                    :actionurl_team_assigned="'order/vp/'+order.order_id+'/team_assigned/update'"
                />
            </div>
        </td>
        <td class="pl-2"><div>{{ order.order_id }}</div></td>
        <td><div>{{ order.order_type }}</div></td>
        <td><div>{{ order.network }}</div></td>
        <td><div>{{ order.program }}</div></td>
        <td class="status order-status">
            <div>
                <span class="status-icon" :class="'orderstatus--' + order.order_status"></span>
                {{ order_status_options.find(x => x.id === order.order_status)?.text }}
            </div>
        </td>
        <template v-for="month in ['March', 'April', 'May', 'June']" v-if="displayMonths.includes(month)">
            <template v-if="getEmailDataByMonth(month)">
                <td class="status actionable" style="cursor: context-menu" @click.stop="showChangeEmailStatusModalForMonth(month)">
                    <div>{{ email_status_options.find(option => option.id === (getEmailDataByMonth(month)?.status))?.text }}</div>
                </td>
                <td>
                    <div>{{ getEmailDataByMonth(month)?.send_at_date ?? '—' }}</div>
                </td>
                <td>
                    <div>{{ getEmailDataByMonth(month)?.send_at_time ?? '—' }}</div>
                </td>
            </template>
            <template v-else>
                <td colspan="3"></td>
            </template>
        </template>

        <div id="email-status-modal">
            <modal v-if="showChangeEmailStatusModal" @close="showChangeEmailStatusModal = false" size="modal-md">
                <template #header>
                    <h2 class="text-lg font-semibold">Change {{ emailStatusMonth }} Email Status</h2>
                </template>
                <template #body>
                    <div v-if="changeEmailStatusError" class="alert text-danger p-3">
                        {{ changeEmailStatusError }}
                    </div>
                    <div :style="`min-height: ${email_status_options.length + 4}em`">
                        <v-select
                            v-model="emailStatuses[emailStatusMonth]"
                            :options="email_status_options"
                            :reduce="option => option.id"
                            label="text"
                        />
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-end">
                        <button @click="showChangeEmailStatusModal = false" class="btn btn-cancel mr-2">
                            Cancel
                        </button>
                        <button
                            @click="updateEmailStatus"
                            class="btn btn-primary"
                            :disabled="updatingEmailStatus"
                            :style="{ cursor: updatingEmailStatus ? 'wait' : null }"
                        >
                            {{ updatingEmailStatus ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </template>
            </modal>
        </div>
    </tr>
</template>

<script>
import axios from "axios";
import Modal from "@/components/Modal.vue";
import CloseIcon from "@/Icons/close.vue";
import DownChevronIcon from "@/Icons/down_chevron.vue";
import UpChevronIcon from "@/Icons/up-chevron.vue";
import SearchIcon from "@/Icons/search.vue";
import CalendarIcon from "@/Icons/calendar.vue";
import Download from "@/Icons/download.vue";

export default {
    name: "TeamAssignmentsViewingRow",

    components: {
        Modal,
        Download,
        CalendarIcon,
        SearchIcon,
        UpChevronIcon,
        DownChevronIcon,
        CloseIcon,
    },

    props: {
        order: {
            type: Object,
            required: true,
        },
        teamAssignmentMembers: {
            type: Array,
            required: true,
        },
        displayMonths: {
            type: Array,
            required: true,
        },
    },

    data() {
        let emailStatuses = {};

        for (let email of this.order.emails) {
            if (typeof email.month !== 'undefined') {
                emailStatuses[email.month] = email.status;
            }
        }

        return {
            showChangeEmailStatusModal: false,
            emailStatuses: emailStatuses,
            emailStatusMonth: null,
            changeEmailStatusError: null,
            updatingEmailStatus: false,

            // TODO store these centrally to avoid duplication
            order_status_options: [
                { text: "In Progress", id: "in_progress" },
                { text: "Order Submitted", id: "submitted" },
                { text: "Need More Information", id: "need_more_info" },
                { text: "User Reported", id: "user_reported" },
                { text: "Order Approved", id: "approved" },
                { text: "Order Completed", id: "order_completed" },
                { text: "Order Canceled", id: "canceled" },
            ],
            email_status_options: [
                { text: "In Progress", id: "in_progress" },
                { text: "Assets Submitted", id: "submitted" },
                { text: "Asset Need More Info", id: "need_more_info" },
                { text: "Internal Review", id: "internal_review" },
                { text: "Approved For Go Live", id: "approved" },
                { text: "Build Underway", id: "building" },
                { text: "Build Complete", id: "build_complete" },
                { text: "Published & Sent", id: "published" },
                { text: "Canceled", id: "canceled" },
            ],
        }
    },

    mounted() {
        // move modals to end of <body> so that they don't act as part of the <tr>
        // this workaround can be removed in Vue 3 when we can use multiple root nodes
        // #vue3
        document.body.appendChild(this.$el.querySelector('#email-status-modal'));
    },

    methods: {
        loadOrder(order_id) {
            window.location = '/order/'+order_id;
        },

        updateEmailStatus() {
            this.updatingEmailStatus = true;

            const newStatus = this.emailStatuses[this.emailStatusMonth];

            axios
                .put('order/vp/'+this.order.order_id+'/email-status/update', {
                    email_status: newStatus,
                    month: this.emailStatusMonth,
                })
                .then(response => {
                    this.getEmailDataByMonth(this.emailStatusMonth).status = newStatus;
                    this.showChangeEmailStatusModal = false;
                })
                .catch(err => {
                    this.changeEmailStatusError = err.response.data.message
                        ?? 'Something went wrong. Please try again later.';
                })
                .finally(() => {
                    this.updatingEmailStatus = false;
                })
        },

        getEmailDataByMonth(month) {
            return this.order.emails.find(email => email.month === month)
        },

        showChangeEmailStatusModalForMonth(month) {
            this.emailStatusMonth = month;
            this.showChangeEmailStatusModal = true;
        },
    },
}
</script>

<style scoped>

td.status.actionable:hover {
    text-decoration: underline;
}

</style>
